// src/components/manager/shared/SiteFormFields.jsx
import React, { useState, useEffect } from 'react';
import { 
  MapPin, Plus, Edit, Trash2, Search, Building, Phone, Mail, X,
  Clock, Users, DollarSign, CheckCircle, AlertCircle, Settings,
  Navigation, Globe, Key, Shield, Wifi, Hammer, GraduationCap,
  Heart, Stethoscope, ShoppingBag, BookOpen, Building2, Factory,
  Store, Landmark, Package2, MapPinCheck, Loader2, RefreshCw
} from 'lucide-react';
import { FileUpload } from '../../common/FileUpload';
import { firestoreService } from '../../../services/firestoreService';
import { organisationService } from '../../../services/organisationService';
import { geocodingService } from '../../../services/geocodingService';

const SITE_TYPES = {
  office: { label: 'Office', color: 'blue', icon: Building },
  branch: { label: 'Branch', color: 'green', icon: MapPin },
  warehouse: { label: 'Warehouse', color: 'yellow', icon: Building },
  store: { label: 'Store/Shop', color: 'purple', icon: Building },
  facility: { label: 'Facility', color: 'indigo', icon: Building },
  venue: { label: 'Event Venue', color: 'pink', icon: Building },
  remote: { label: 'Remote Location', color: 'gray', icon: Globe },
  construction: { label: 'Construction Site', color: 'orange', icon: Hammer },
  school: { label: 'School/Educational', color: 'emerald', icon: GraduationCap },
  hospital: { label: 'Hospital/Medical', color: 'red', icon: Heart },
  clinic: { label: 'Clinic/Healthcare', color: 'pink', icon: Stethoscope },
  mall: { label: 'Shopping Mall', color: 'violet', icon: ShoppingBag },
  university: { label: 'University/Campus', color: 'blue', icon: BookOpen },
  officeBlock: { label: 'Office Block/Tower', color: 'slate', icon: Building2 },
  industrial: { label: 'Industrial Site', color: 'zinc', icon: Factory },
  retail: { label: 'Retail Location', color: 'teal', icon: Store },
  government: { label: 'Government Facility', color: 'amber', icon: Landmark }
};

const SITE_STATUS = {
  active: { label: 'Active', color: 'green', icon: CheckCircle },
  inactive: { label: 'Inactive', color: 'red', icon: AlertCircle },
  maintenance: { label: 'Maintenance', color: 'yellow', icon: Settings },
  planned: { label: 'Planned', color: 'blue', icon: Clock }
};

export const SiteFormFields = ({ bookId, templateType, onUpdate, accessLevel = 'internal' }) => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [saving, setSaving] = useState(false);

  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [siteAssets, setSiteAssets] = useState([]);
  const [syncingAssets, setSyncingAssets] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    type: 'office',
    status: 'active',
    description: '',
    address: {
      street: '',
      city: '',
      province: '',
      postalCode: '',
      country: 'South Africa'
    },
    contact: {
      phone: '',
      email: '',
      manager: ''
    },
    coordinates: {
      latitude: '',
      longitude: '',
      placeId: ''
    },

    workSite: {
      isWorkSite: false,
      clientName: '',
      projectName: '',
      projectNumber: '',
      contractorCompany: '',
      siteManager: '',
      startDate: '',
      endDate: '',
      projectValue: '',
      safetyOfficer: '',
      permitNumbers: [],
      accessRequirements: '',
      workingHours: {
        monday: { start: '07:00', end: '17:00', working: true },
        tuesday: { start: '07:00', end: '17:00', working: true },
        wednesday: { start: '07:00', end: '17:00', working: true },
        thursday: { start: '07:00', end: '17:00', working: true },
        friday: { start: '07:00', end: '17:00', working: true },
        saturday: { start: '08:00', end: '13:00', working: false },
        sunday: { start: '08:00', end: '13:00', working: false }
      }
    },

    assetRegister: {
      enabled: true,
      assets: [], // Will be populated from pb-asset-management collection
      lastSyncDate: null
    },

    operatingHours: {
      monday: { open: '09:00', close: '17:00', closed: false },
      tuesday: { open: '09:00', close: '17:00', closed: false },
      wednesday: { open: '09:00', close: '17:00', closed: false },
      thursday: { open: '09:00', close: '17:00', closed: false },
      friday: { open: '09:00', close: '17:00', closed: false },
      saturday: { open: '09:00', close: '13:00', closed: false },
      sunday: { open: '09:00', close: '17:00', closed: true }
    },
    capacity: '',
    amenities: [],
    images: [],
    notes: ''
  });

  const AMENITIES = [
    'Parking', 'WiFi', 'Security', 'Air Conditioning', 'Heating', 'Reception',
    'Conference Room', 'Kitchen', 'Restrooms', 'Elevator', 'Wheelchair Access',
    'Loading Dock', 'Storage', 'Break Room', 'Cafeteria', 'Gym'
  ];

  useEffect(() => {
    loadSiteData();
    initializeGeocoding();
  }, [bookId]);

  const loadSiteData = async () => {
    try {
      setLoading(true);
      const data = await organisationService.getManagementFeatureData(bookId, 'sites');
      setSites(data?.sites || []);
    } catch (error) {
      console.error('Error loading site data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSiteData = async (updatedSites) => {
    try {
      await organisationService.updateManagementFeatureData(bookId, 'sites', {
        sites: updatedSites,
        lastUpdated: new Date(),
        totalSites: updatedSites.length,
        activeSites: updatedSites.filter(s => s.status === 'active').length
      });
      onUpdate?.();
    } catch (error) {
      console.error('Error saving site data:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (editingSite && formData.assetRegister.enabled) {
      handleSyncAssets();
    }
  }, [editingSite]);

  const initializeGeocoding = async () => {
    try {
      // Get API key from book configuration
      const bookConfig = await firestoreService.getBook(bookId);
      const apiKey = bookConfig?.integrations?.googleMapsApiKey;
      
      if (apiKey) {
        await geocodingService.initialize(apiKey);
      }
    } catch (error) {
      console.error('Failed to initialize geocoding:', error);
    }
  };

  const handleAddressSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;
    
    setGeocodingLoading(true);
    try {
      const result = await geocodingService.geocodeAddress(searchQuery);
      
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          formatted: result.address,
          street: `${result.addressComponents.streetNumber || ''} ${result.addressComponents.route || ''}`.trim(),
          city: result.addressComponents.city || prev.address.city,
          province: result.addressComponents.province || prev.address.province,
          postalCode: result.addressComponents.postalCode || prev.address.postalCode,
          country: result.addressComponents.country || prev.address.country
        },
        coordinates: {
          ...prev.coordinates,
          ...result.coordinates,
          placeId: result.placeId
        }
      }));
    } catch (error) {
      alert('Address search failed: ' + error.message);
    } finally {
      setGeocodingLoading(false);
    }
  };

  const handleReverseGeocode = async () => {
    if (!formData.coordinates.latitude || !formData.coordinates.longitude) return;
    
    setGeocodingLoading(true);
    try {
      const result = await geocodingService.reverseGeocode(
        formData.coordinates.latitude,
        formData.coordinates.longitude
      );
      
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          formatted: result.address,
          street: `${result.addressComponents.streetNumber || ''} ${result.addressComponents.route || ''}`.trim(),
          city: result.addressComponents.city || prev.address.city,
          province: result.addressComponents.province || prev.address.province,
          postalCode: result.addressComponents.postalCode || prev.address.postalCode,
          country: result.addressComponents.country || prev.address.country
        }
      }));
    } catch (error) {
      alert('Reverse geocoding failed: ' + error.message);
    } finally {
      setGeocodingLoading(false);
    }
  };

  const handleSyncAssets = async () => {
    setSyncingAssets(true);
    try {
      // Get all assets from the asset management system
      const assetData = await firestoreService.getDocument('pb-asset-management', bookId);
      const allAssets = assetData?.assets || [];
      
      // Filter assets assigned to this site
      const currentSiteId = editingSite?.id || formData.id;
      const assetsAtSite = allAssets.filter(asset => asset.location === currentSiteId);
      
      setSiteAssets(assetsAtSite);
      
      // Update the asset register in form data
      setFormData(prev => ({
        ...prev,
        assetRegister: {
          ...prev.assetRegister,
          assets: assetsAtSite.map(a => a.id),
          lastSyncDate: new Date()
        }
      }));
    } catch (error) {
      alert('Failed to sync assets: ' + error.message);
    } finally {
      setSyncingAssets(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const siteData = {
        ...formData,
        id: editingSite?.id || Date.now().toString(),
        createdAt: editingSite?.createdAt || new Date(),
        updatedAt: new Date(),
        capacity: formData.capacity ? parseInt(formData.capacity) : 0
      };

      let updatedSites;
      if (editingSite) {
        updatedSites = sites.map(site => 
          site.id === editingSite.id ? siteData : site
        );
      } else {
        updatedSites = [...sites, siteData];
      }

      await saveSiteData(updatedSites);
      setSites(updatedSites);
      handleCloseForm();
    } catch (error) {
      alert('Error saving site: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (siteId) => {
    if (!window.confirm('Are you sure you want to delete this site?')) return;

    try {
      const updatedSites = sites.filter(site => site.id !== siteId);
      await saveSiteData(updatedSites);
      setSites(updatedSites);
    } catch (error) {
      alert('Error deleting site: ' + error.message);
    }
  };

  const handleEdit = (site) => {
    setEditingSite(site);
    setFormData(site);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingSite(null);
    setFormData({
      name: '', type: 'office', status: 'active', description: '',
      address: { street: '', city: '', province: '', postalCode: '', country: 'South Africa' },
      contact: { phone: '', email: '', manager: '' },
      coordinates: { latitude: '', longitude: '' },
      operatingHours: {
        monday: { open: '09:00', close: '17:00', closed: false },
        tuesday: { open: '09:00', close: '17:00', closed: false },
        wednesday: { open: '09:00', close: '17:00', closed: false },
        thursday: { open: '09:00', close: '17:00', closed: false },
        friday: { open: '09:00', close: '17:00', closed: false },
        saturday: { open: '09:00', close: '13:00', closed: false },
        sunday: { open: '09:00', close: '17:00', closed: true }
      },
      capacity: '', amenities: [], images: [], notes: ''
    });
  };

  const handleImageUpload = async (files, error) => {
    if (error) {
      alert('Error uploading images: ' + error);
      return;
    }

    try {
      const results = await firestoreService.uploadMultipleFiles(
        Array.isArray(files) ? files : [files],
        (file) => firestoreService.uploadFile(file, `pb-${bookId}/sites/images`),
        (current, total, result, error) => {
          if (error) console.error('Upload error:', error);
        }
      );

      const successfulUploads = results.filter(r => r.success !== false);
      const newImages = successfulUploads.map(result => ({
        url: result.url,
        fileName: result.fileName,
        uploadedAt: new Date()
      }));

      setFormData(prev => ({
        ...prev,
        images: [...(prev.images || []), ...newImages]
      }));
    } catch (error) {
      alert('Error uploading images: ' + error.message);
    }
  };

  const handleAmenityToggle = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const filteredSites = sites.filter(site => {
    const matchesSearch = site.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         site.address.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         site.contact.manager.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || site.type === filterType;
    const matchesStatus = filterStatus === 'all' || site.status === filterStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <MapPin className="h-7 w-7 text-blue-600" />
            Site Management
          </h3>
          <p className="text-gray-600 mt-1">Manage multiple locations, branches, and venues</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Site
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Sites</p>
              <p className="text-2xl font-bold text-gray-900">{sites.length}</p>
            </div>
            <MapPin className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Sites</p>
              <p className="text-2xl font-bold text-green-600">
                {sites.filter(s => s.status === 'active').length}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Capacity</p>
              <p className="text-2xl font-bold text-purple-600">
                {sites.reduce((sum, site) => sum + (site.capacity || 0), 0)}
              </p>
            </div>
            <Users className="h-8 w-8 text-purple-600" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Provinces</p>
              <p className="text-2xl font-bold text-orange-600">
                {new Set(sites.map(s => s.address.province).filter(p => p)).size}
              </p>
            </div>
            <Globe className="h-8 w-8 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border space-y-4 md:space-y-0 md:flex md:items-center md:gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search sites..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Types</option>
          {Object.entries(SITE_TYPES).map(([key, type]) => (
            <option key={key} value={key}>{type.label}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          {Object.entries(SITE_STATUS).map(([key, status]) => (
            <option key={key} value={key}>{status.label}</option>
          ))}
        </select>
      </div>

      {/* Sites Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredSites.map(site => {
          const typeConfig = SITE_TYPES[site.type] || SITE_TYPES.office;
          const statusConfig = SITE_STATUS[site.status] || SITE_STATUS.active;
          const TypeIcon = typeConfig.icon;
          const StatusIcon = statusConfig.icon;
          
          return (
            <div key={site.id} className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow">
              {/* Site Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-${typeConfig.color}-100`}>
                    <TypeIcon className={`h-5 w-5 text-${typeConfig.color}-600`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{site.name}</h4>
                    <p className="text-sm text-gray-600">{typeConfig.label}</p>
                  </div>
                </div>
                
                <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-${statusConfig.color}-100 text-${statusConfig.color}-700`}>
                  <StatusIcon className="h-3 w-3" />
                  {statusConfig.label}
                </div>
              </div>

              {/* Site Image */}
              {site.images && site.images.length > 0 && (
                <div className="mb-4">
                  <img 
                    src={site.images[0].url} 
                    alt={site.name}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                </div>
              )}

              {/* Site Details */}
              <div className="space-y-2 mb-4">
                <div className="flex items-start gap-2 text-sm text-gray-600">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p>{site.address.street}</p>
                    <p>{site.address.city}, {site.address.province} {site.address.postalCode}</p>
                  </div>
                </div>

                {site.contact.manager && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="h-4 w-4" />
                    Manager: {site.contact.manager}
                  </div>
                )}

                {site.contact.phone && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone className="h-4 w-4" />
                    {site.contact.phone}
                  </div>
                )}

                {site.capacity && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="h-4 w-4" />
                    Capacity: {site.capacity} people
                  </div>
                )}
              </div>

              {/* Amenities */}
              {site.amenities && site.amenities.length > 0 && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-1">
                    {site.amenities.slice(0, 4).map(amenity => (
                      <span key={amenity} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                        {amenity}
                      </span>
                    ))}
                    {site.amenities.length > 4 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                        +{site.amenities.length - 4} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Operating Hours */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                  <Clock className="h-4 w-4" />
                  Today's Hours
                </div>
                {(() => {
                  const today = new Date().toLocaleDateString('en', { weekday: 'lowercase' });
                  const todayHours = site.operatingHours?.[today];
                  if (todayHours?.closed) {
                    return <p className="text-sm text-red-600">Closed</p>;
                  } else if (todayHours) {
                    return <p className="text-sm text-gray-900">{todayHours.open} - {todayHours.close}</p>;
                  }
                  return <p className="text-sm text-gray-600">Hours not set</p>;
                })()}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-2">
                  {site.coordinates.latitude && site.coordinates.longitude && (
                    <a
                      href={`https://maps.google.com/?q=${site.coordinates.latitude},${site.coordinates.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800"
                      title="View on map"
                    >
                      <Navigation className="h-4 w-4" />
                    </a>
                  )}
                </div>
                
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEdit(site)}
                    className="p-1 text-gray-400 hover:text-blue-600 rounded"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(site.id)}
                    className="p-1 text-gray-400 hover:text-red-600 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredSites.length === 0 && (
        <div className="text-center py-8">
          <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No sites found</h3>
          <p className="text-gray-600 mb-4">Get started by adding your first site.</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Add Site
          </button>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-xl font-semibold">
                  {editingSite ? 'Edit Site' : 'Add Site'}
                </h3>
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Form Content */}
              <div className="p-6 space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.entries(SITE_TYPES).map(([key, type]) => (
                        <option key={key} value={key}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.entries(SITE_STATUS).map(([key, status]) => (
                        <option key={key} value={key}>{status.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>

                {/* Address */}
                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Address Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
                      <input
                        type="text"
                        value={formData.address.street}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, street: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                      <input
                        type="text"
                        value={formData.address.city}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, city: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Province</label>
                      <input
                        type="text"
                        value={formData.address.province}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, province: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Postal Code</label>
                      <input
                        type="text"
                        value={formData.address.postalCode}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, postalCode: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                      <input
                        type="text"
                        value={formData.address.country}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, country: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                      <input
                        type="tel"
                        value={formData.contact.phone}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          contact: { ...prev.contact, phone: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={formData.contact.email}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          contact: { ...prev.contact, email: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Site Manager</label>
                      <input
                        type="text"
                        value={formData.contact.manager}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          contact: { ...prev.contact, manager: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Capacity */}
                <div className="border-t pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Capacity (people)</label>
                      <input
                        type="number"
                        value={formData.capacity}
                        onChange={(e) => setFormData(prev => ({ ...prev, capacity: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Amenities */}
                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Amenities</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {AMENITIES.map(amenity => (
                      <label key={amenity} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.amenities.includes(amenity)}
                          onChange={() => handleAmenityToggle(amenity)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{amenity}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Images */}
                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Site Images</h4>
                  
                  <FileUpload
                    onFileSelect={handleImageUpload}
                    accept="image/*"
                    multiple
                    maxSize={5}
                    className="mb-4"
                  >
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
                      <MapPin className="h-8 w-8 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-2">Upload Site Images</p>
                      <p className="text-sm text-gray-500">Drag & drop or click to select multiple images</p>
                    </div>
                  </FileUpload>

                  {formData.images && formData.images.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {formData.images.map((image, index) => (
                        <div key={index} className="relative">
                          <img 
                            src={image.url} 
                            alt={`Site ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              images: prev.images.filter((_, i) => i !== index)
                            }))}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              <div className="border-t pt-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <MapPinCheck className="h-5 w-5 text-blue-600" />
                  Address & Location
                </h4>
                
                <div className="space-y-4">
                  {/* Address Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search Address
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Enter address to search..."
                        className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddressSearch(e.target.value);
                          }
                        }}
                      />
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      {geocodingLoading && (
                        <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-blue-600 animate-spin" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Press Enter to search and auto-fill address details
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Formatted Address
                      </label>
                      <input
                        type="text"
                        value={formData.address.formatted}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, formatted: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Full formatted address"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Street Address
                      </label>
                      <input
                        type="text"
                        value={formData.address.street}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, street: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Street number and name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                      <input
                        type="text"
                        value={formData.address.city}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, city: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="City"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Province</label>
                      <input
                        type="text"
                        value={formData.address.province}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, province: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Province"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Postal Code</label>
                      <input
                        type="text"
                        value={formData.address.postalCode}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, postalCode: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Postal code"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                      <input
                        type="text"
                        value={formData.address.country}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, country: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Country"
                      />
                    </div>
                  </div>

                  {/* Coordinates */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Latitude</label>
                      <input
                        type="number"
                        step="any"
                        value={formData.coordinates.latitude}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          coordinates: { ...prev.coordinates, latitude: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="-26.2041"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Longitude</label>
                      <input
                        type="number"
                        step="any"
                        value={formData.coordinates.longitude}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          coordinates: { ...prev.coordinates, longitude: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="28.0473"
                      />
                    </div>

                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleReverseGeocode}
                        disabled={!formData.coordinates.latitude || !formData.coordinates.longitude}
                        className="w-full px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        <Navigation className="h-4 w-4" />
                        Get Address
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Work Site Information */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Hammer className="h-5 w-5 text-orange-600" />
                    Work Site Details
                  </h4>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.workSite.isWorkSite}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        workSite: { ...prev.workSite, isWorkSite: e.target.checked }
                      }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">This is a work site</span>
                  </label>
                </div>

                {formData.workSite.isWorkSite && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Client Name</label>
                        <input
                          type="text"
                          value={formData.workSite.clientName}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            workSite: { ...prev.workSite, clientName: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Client organization name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Project Name</label>
                        <input
                          type="text"
                          value={formData.workSite.projectName}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            workSite: { ...prev.workSite, projectName: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Project title"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Project Number</label>
                        <input
                          type="text"
                          value={formData.workSite.projectNumber}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            workSite: { ...prev.workSite, projectNumber: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Unique project identifier"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Contractor Company</label>
                        <input
                          type="text"
                          value={formData.workSite.contractorCompany}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            workSite: { ...prev.workSite, contractorCompany: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Main contractor"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Site Manager</label>
                        <input
                          type="text"
                          value={formData.workSite.siteManager}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            workSite: { ...prev.workSite, siteManager: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="On-site manager name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Safety Officer</label>
                        <input
                          type="text"
                          value={formData.workSite.safetyOfficer}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            workSite: { ...prev.workSite, safetyOfficer: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Safety officer name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                        <input
                          type="date"
                          value={formData.workSite.startDate}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            workSite: { ...prev.workSite, startDate: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                        <input
                          type="date"
                          value={formData.workSite.endDate}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            workSite: { ...prev.workSite, endDate: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Project Value (ZAR)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.workSite.projectValue}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            workSite: { ...prev.workSite, projectValue: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="0.00"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Access Requirements</label>
                        <textarea
                          value={formData.workSite.accessRequirements}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            workSite: { ...prev.workSite, accessRequirements: e.target.value }
                          }))}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Security clearance, PPE requirements, access procedures..."
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Asset Register */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Package2 className="h-5 w-5 text-green-600" />
                    Asset Register
                  </h4>
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={handleSyncAssets}
                      disabled={syncingAssets}
                      className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                    >
                      {syncingAssets ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      Sync Assets
                    </button>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.assetRegister.enabled}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          assetRegister: { ...prev.assetRegister, enabled: e.target.checked }
                        }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Enable asset tracking</span>
                    </label>
                  </div>
                </div>

                {formData.assetRegister.enabled && (
                  <div className="space-y-4">
                    {/* Asset Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-blue-900">
                          {siteAssets.filter(a => a.status === 'active').length}
                        </div>
                        <div className="text-sm text-blue-700">Active Assets</div>
                      </div>
                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-yellow-900">
                          {siteAssets.filter(a => a.status === 'maintenance').length}
                        </div>
                        <div className="text-sm text-yellow-700">In Maintenance</div>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-green-900">
                          {siteAssets.filter(a => a.currentValue > 0).reduce((sum, a) => sum + (a.currentValue || 0), 0).toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' })}
                        </div>
                        <div className="text-sm text-green-700">Total Value</div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">
                          {formData.assetRegister.lastSyncDate ? 
                            new Date(formData.assetRegister.lastSyncDate).toLocaleDateString() : 
                            'Never'
                          }
                        </div>
                        <div className="text-sm text-gray-700">Last Sync</div>
                      </div>
                    </div>

                    {/* Assets List */}
                    {siteAssets.length > 0 ? (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                          <h5 className="font-medium text-gray-900">Assets at this Site</h5>
                        </div>
                        <div className="divide-y divide-gray-200 max-h-64 overflow-y-auto">
                          {siteAssets.map(asset => (
                            <div key={asset.id} className="px-4 py-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-3 h-3 rounded-full ${
                                  asset.status === 'active' ? 'bg-green-500' :
                                  asset.status === 'maintenance' ? 'bg-yellow-500' : 'bg-red-500'
                                }`} />
                                <div>
                                  <div className="font-medium text-gray-900">{asset.name}</div>
                                  <div className="text-sm text-gray-500">{asset.category} • {asset.serialNumber}</div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-medium text-gray-900">
                                  {asset.currentValue ? 
                                    asset.currentValue.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' }) : 
                                    'N/A'
                                  }
                                </div>
                                <div className="text-sm text-gray-500 capitalize">{asset.status}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <Package2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="font-medium text-gray-900 mb-2">No assets found</h3>
                        <p className="text-gray-600">Assets will appear here when they are assigned to this site.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {editingSite ? 'Update Site' : 'Add Site'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};